import { useState } from 'react';
import { isAxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { publishManualRate, updateManualRate } from '../../api/exchangeRate';
import { ExchangeRate } from '../../types/exchangeRate';
import { formatMoney, formatRate, formatRateDateTime, toDateTimeLocal } from '../../utils/exchangeRates';

const MIN_RATE = 50;
const MAX_RATE = 1000;
// Registration Fee 500 + SSCL — a familiar figure for the preview example.
const EXAMPLE_USD = 512.82;

const errorText = (err: unknown) =>
  isAxiosError(err) ? (err.response?.data?.message ?? "Couldn't save the rate.") : "Couldn't save the rate.";

/** Client-side checks, mirroring the server's (which still has the last word). */
export const manualRateProblems = (rate: string, effectiveAt: string, reason: string) => {
  const problems: { rate?: string; effectiveAt?: string; reason?: string } = {};
  const value = Number(rate);
  if (rate.trim() === '' || !Number.isFinite(value) || value <= 0) problems.rate = 'Enter a positive number.';
  else if (value < MIN_RATE || value > MAX_RATE) problems.rate = `Enter a USD/LKR rate between ${MIN_RATE} and ${MAX_RATE}.`;
  else if (!/^\d+(\.\d{1,4})?$/.test(rate.trim())) problems.rate = 'Use at most 4 decimal places.';
  const when = new Date(effectiveAt);
  if (!effectiveAt || Number.isNaN(when.getTime())) problems.effectiveAt = 'Enter a valid date and time.';
  else if (when.getTime() > Date.now() + 5 * 60 * 1000) problems.effectiveAt = "The effective date can't be in the future.";
  if (reason.trim().length < 5) problems.reason = 'Give a reason of at least 5 characters.';
  return problems;
};

/** "Edit Exchange Rate" (Phase 104) — publish a manual USD/LKR rate, which
 * may be backdated, with a reason. Two steps: the form, then a Preview of
 * exactly what will be saved next to the current automatic rate, then Save.
 * A manual rate never replaces the automatic Nations Trust rate: it applies
 * only where someone selects it for a payment, invoice or receipt. With
 * `editing`, corrects a manual rate that nothing uses yet. */
export const EditExchangeRateDialog = ({
  open,
  onClose,
  currentRate,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  currentRate: ExchangeRate | null;
  editing?: ExchangeRate | null;
}) => {
  const queryClient = useQueryClient();
  const [rate, setRate] = useState(editing ? String(Number(editing.rate)) : '');
  const [effectiveAt, setEffectiveAt] = useState(
    toDateTimeLocal(editing ? new Date(editing.sourcePublishedAt) : new Date()),
  );
  const [reason, setReason] = useState(editing?.reason ?? '');
  const [step, setStep] = useState<'form' | 'preview'>('form');
  const [touched, setTouched] = useState(false);

  const problems = manualRateProblems(rate, effectiveAt, reason);
  const valid = Object.keys(problems).length === 0;

  const save = useMutation({
    mutationFn: () => {
      const input = { rate: Number(rate), effectiveAt: new Date(effectiveAt).toISOString(), reason: reason.trim() };
      return editing ? updateManualRate(editing.id, input) : publishManualRate(input);
    },
    onSuccess: () => {
      for (const key of ['exchange-rate-current', 'exchange-rate-options', 'exchange-rate-history', 'exchange-rate-latest-manual']) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
      close();
    },
  });

  const close = () => {
    if (save.isPending) return;
    setStep('form');
    setTouched(false);
    save.reset();
    if (!editing) {
      setRate('');
      setReason('');
      setEffectiveAt(toDateTimeLocal(new Date()));
    }
    onClose();
  };

  const newRate = Number(rate);
  const current = currentRate ? Number(currentRate.rate) : null;
  const difference = current ? ((newRate - current) / current) * 100 : null;
  const backdated = new Date(effectiveAt).getTime() < Date.now() - 60 * 60 * 1000;

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm" aria-labelledby="edit-exchange-rate-title">
      <DialogTitle id="edit-exchange-rate-title">
        {editing ? 'Correct manual exchange rate' : 'Edit Exchange Rate'}
      </DialogTitle>
      <DialogContent dividers>
        {step === 'form' ? (
          <Stack spacing={2}>
            <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Current automatic rate:</strong>{' '}
                {currentRate
                  ? `Rs. ${formatRate(currentRate.rate)} — effective ${formatRateDateTime(currentRate.sourcePublishedAt)} — ${currentRate.source}, TT Selling`
                  : 'none received yet'}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              A manual rate is kept alongside the automatic Nations Trust rate — it doesn't replace it, and it doesn't
              change any existing payment, invoice or receipt. It's used only where someone selects it.
            </Typography>
            <TextField
              label="Manual rate (LKR per 1 USD)"
              type="number"
              inputProps={{ step: '0.0001', min: MIN_RATE, max: MAX_RATE, inputMode: 'decimal' }}
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              error={touched && !!problems.rate}
              helperText={touched && problems.rate}
              fullWidth
            />
            <TextField
              label="Effective date and time"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: toDateTimeLocal(new Date()) }}
              value={effectiveAt}
              onChange={(e) => setEffectiveAt(e.target.value)}
              error={touched && !!problems.effectiveAt}
              helperText={(touched && problems.effectiveAt) || 'May be in the past (backdated).'}
              fullWidth
            />
            <TextField
              label="Reason for the change"
              multiline
              minRows={2}
              inputProps={{ maxLength: 500 }}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              error={touched && !!problems.reason}
              helperText={(touched && problems.reason) || 'Recorded in the audit log.'}
              fullWidth
            />
          </Stack>
        ) : (
          <Stack spacing={2} data-testid="manual-rate-preview">
            <Typography variant="subtitle2">Check before saving</Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Current automatic rate</TableCell>
                  <TableCell align="right">
                    {currentRate ? `Rs. ${formatRate(currentRate.rate)} (${formatRateDateTime(currentRate.sourcePublishedAt)})` : '—'}
                  </TableCell>
                </TableRow>
                {editing && (
                  <TableRow>
                    <TableCell>Manual rate before this correction</TableCell>
                    <TableCell align="right">
                      Rs. {formatRate(editing.rate)} ({formatRateDateTime(editing.sourcePublishedAt)})
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell>
                    <strong>Manual rate</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Rs. {formatRate(newRate)}</strong>
                    {difference != null && ` (${difference >= 0 ? '+' : ''}${difference.toFixed(2)}% vs automatic)`}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Effective</TableCell>
                  <TableCell align="right">
                    {formatRateDateTime(new Date(effectiveAt))}
                    {backdated ? ' (backdated)' : ''}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Source</TableCell>
                  <TableCell align="right">Manual — published by you</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Reason</TableCell>
                  <TableCell align="right" sx={{ wordBreak: 'break-word' }}>
                    {reason.trim()}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Example: USD {formatMoney(EXAMPLE_USD)}</TableCell>
                  <TableCell align="right">
                    LKR {formatMoney(Math.round(EXAMPLE_USD * newRate * 100) / 100)}
                    {current != null && ` (automatic: LKR ${formatMoney(Math.round(EXAMPLE_USD * current * 100) / 100)})`}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Alert severity="info">
              Saving publishes this rate for selection. The automatic rate stays current for new calculations, and
              existing payments, invoices and receipts don't change.
            </Alert>
            {save.isError && <Alert severity="error">{errorText(save.error)}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={close} disabled={save.isPending}>
          Cancel
        </Button>
        {step === 'form' ? (
          <Button
            variant="contained"
            onClick={() => {
              setTouched(true);
              if (valid) setStep('preview');
            }}
          >
            Preview
          </Button>
        ) : (
          <>
            <Button onClick={() => setStep('form')} disabled={save.isPending}>
              Back
            </Button>
            <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : editing ? 'Update rate' : 'Save rate'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
